import CardFragment, {
  CardFragmentCommonProps,
} from "@coworker/apprestructured/src/task-management/components/TaskCard/card-fragments/CardFragment";
import LoadingCardFragments from "@coworker/apprestructured/src/task-management/components/TaskCard/card-fragments/LoadingCardFragments/LoadingCardFragments";
import LoadingTaskCard from "@coworker/apprestructured/src/task-management/components/TaskCard/components/LoadingTaskCard/LoadingTaskCard";
import Start from "@coworker/apprestructured/src/task-management/components/TaskCard/components/Start/Start";
import useTaskCardAssigneeLabel from "@coworker/apprestructured/src/task-management/components/TaskCard/useTaskCardAssigneeLabel";
import ClosedTaskCard from "@coworker/apprestructured/src/task-management/components/TaskCard/variants/ClosedTaskCard/ClosedTaskCard";
import CompletedTaskCard from "@coworker/apprestructured/src/task-management/components/TaskCard/variants/CompletedTaskCard/CompletedTaskCard";
import PendingTaskCard from "@coworker/apprestructured/src/task-management/components/TaskCard/variants/PendingTaskCard/PendingTaskCard";
import {
  isLoading,
  LOADING,
  Loading,
} from "@coworker/apprestructured/src/task-management/loading";
import { useAllTaskTypes } from "@coworker/apprestructured/src/task-management/services/useTaskTypes";
import { Task } from "@coworker/apprestructured/src/task-management/types/Task";
import { TaskType } from "@coworker/apprestructured/src/task-management/types/TaskType";
import { timestampToTimeDiff } from "@coworker/apprestructured/src/task-management/util";
import { useTranslation } from "@coworker/locales";
import { useMemo } from "react";

type TaskCardProps = {
  task: Task | Loading;
};

/**
 * This is a convenience component so that it is easy to render a task card
 * without fetching all data separately.
 *
 * Apart from the accepted 'task' prop, this component loads of the data it needs.
 * These are very expensive backend calls and is a sane thing to do only because
 * these are all cached, meaning rendering many instances of this component will
 * only result in a couple of backend calls.
 */
function TaskCard({ task }: TaskCardProps) {
  const { t } = useTranslation();

  const { data: taskTypes } = useAllTaskTypes();
  const assigneeLabel = useTaskCardAssigneeLabel(task);

  const taskType = useMemo(() => {
    if (isLoading(task)) {
      return LOADING;
    } else {
      return (
        (taskTypes?.find(({ id }) => id === task.task_type_id) as TaskType) ??
        LOADING
      );
    }
  }, [task, taskTypes]);

  const cardFragmentCommonProps: CardFragmentCommonProps = useMemo(() => {
    return { task, taskType };
  }, [task, taskType]);

  const fragments = useMemo(() => {
    if (isLoading(taskType)) {
      return <LoadingCardFragments />;
    } else {
      return taskType.card_fragments.map((cardFragmentConfig, i) => {
        return (
          <CardFragment
            key={i}
            config={cardFragmentConfig}
            commonProps={cardFragmentCommonProps}
          />
        );
      });
    }
  }, [cardFragmentCommonProps, taskType]);

  const startElement = useMemo(() => {
    if (isLoading(task)) {
      return LOADING;
    } else if (task.start === null) {
      return null;
    } else {
      return <Start date={task.start} />;
    }
  }, [task]);

  if (isLoading(task)) {
    return <LoadingTaskCard />;
  }

  const taskTypeValue = isLoading(taskType) ? LOADING : t(taskType.label);

  const hasImage = Boolean(task.image_ids?.length);
  const hasFile = Boolean(task.file_ids?.length);
  const hasComment = Boolean(task.comment_ids?.length);
  const safetyAlarm = task.details?.["safety_alarm"] === true;

  switch (task.state) {
    case "pending": {
      return (
        <PendingTaskCard
          taskType={taskTypeValue}
          priority={task.priority}
          safetyAlarm={safetyAlarm}
          hasImage={hasImage}
          hasFile={hasFile}
          hasComment={hasComment}
          date={`${t("createdString")} ${timestampToTimeDiff({
            dateTime: task.created_at,
            t,
          })}`}
          assigned={assigneeLabel}
          startElement={startElement}
        >
          {fragments}
        </PendingTaskCard>
      );
    }
    case "completed": {
      return (
        <CompletedTaskCard
          taskType={taskTypeValue}
          priority={task.priority}
          safetyAlarm={safetyAlarm}
          hasImage={hasImage}
          hasFile={hasFile}
          hasComment={hasComment}
          date={`${t("completd3String")} ${timestampToTimeDiff({
            dateTime: task.completed_at,
            t,
          })}`}
          assigned={assigneeLabel}
        >
          {fragments}
        </CompletedTaskCard>
      );
    }
    case "closed": {
      return (
        <ClosedTaskCard
          taskType={taskTypeValue}
          priority={task.priority}
          safetyAlarm={safetyAlarm}
          hasImage={hasImage}
          hasFile={hasFile}
          hasComment={hasComment}
          date={`${t("closedFirstUppercaseString")} ${timestampToTimeDiff({
            dateTime: task.closed_at,
            t,
          })}`}
          assigned={assigneeLabel}
        >
          {fragments}
        </ClosedTaskCard>
      );
    }
  }
}

export default TaskCard;
